// CRM Personas — rehouses the classic Personas screen into the Deliverability shell.
// A persona is a sending identity (display name, title, reply-to, signature) backed by
// N of the client's mailboxes. Recipients see one identity. No rotation-strategy control:
// each mailbox sends in its own right, sticky per conversation.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import ReplyEditor from '../../components/ReplyEditor';

const ST = { active: 'crmpe-dot-green', error: 'crmpe-dot-rose', paused: 'crmpe-dot-amber' };

export default function CrmPersonas({ clientId }) {
  const { getToken } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [clientMailboxes, setClientMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }, [getToken]);

  const loadList = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const headers = await authHeaders();
      const [pRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/clients/${clientId}/personas`, { headers }),
        fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
      ]);
      const pData = await pRes.json();
      const mData = await mRes.json();
      setPersonas(Array.isArray(pData) ? pData : []);
      setClientMailboxes(Array.isArray(mData) ? mData : []);
      if (Array.isArray(pData) && pData.length) setSelectedId(prev => prev ?? pData[0].id);
    } catch (e) {
      toast.error('Could not load personas');
    } finally {
      setLoading(false);
    }
  }, [clientId, authHeaders]);

  const loadDetail = useCallback(async (pid) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${pid}`, { headers });
      setDetail(await res.json());
    } catch (e) {
      toast.error('Could not load persona');
    }
  }, [authHeaders]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId, loadDetail]);

  const createPersona = async () => {
    if (!newName.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/clients/${clientId}/personas`, {
        method: 'POST', headers, body: JSON.stringify({ display_name: newName.trim() }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setNewName('');
      await loadList();
      setSelectedId(created.id);
      toast.success('Persona created');
    } catch (e) {
      toast.error('Create failed');
    } finally {
      setCreating(false);
    }
  };

  const saveDetail = async (patch) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${detail.id}`, {
        method: 'PUT', headers, body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setDetail(d => ({ ...d, ...updated }));
      await loadList();
      toast.success('Saved');
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const deletePersona = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete persona "${detail.display_name}"? In-flight sends keep running; it stops being selectable for new sends.`)) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${detail.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      setSelectedId(null);
      await loadList();
      toast.success('Persona removed');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const toggleMailbox = async (mailboxId, isBacking) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${detail.id}/mailboxes/${mailboxId}`, {
        method: isBacking ? 'DELETE' : 'POST', headers,
        body: isBacking ? undefined : JSON.stringify({ priority: 0 }),
      });
      if (!res.ok) throw new Error();
      await loadDetail(detail.id);
      await loadList();
    } catch (e) {
      toast.error('Could not update backing mailbox');
    }
  };

  const backingIds = new Set((detail?.mailboxes || []).filter(m => m.is_active).map(m => m.id));

  return (
    <div className="crmpe-root">
      <div className="crmpe-rail">
        <div className="crmpe-rail-head">
          <span className="crmpe-rail-title">Personas ({personas.length})</span>
        </div>
        <div className="crmpe-rail-new">
          <input
            className="crmpe-rail-input"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createPersona(); }}
            placeholder="New persona name"
          />
          <button className="crmpe-btn crmpe-btn--primary crmpe-btn--sm" onClick={createPersona} disabled={creating}>Add</button>
        </div>
        <div className="crmpe-rail-list">
          {loading ? (
            <div className="crmpe-rail-empty">Loading…</div>
          ) : personas.length === 0 ? (
            <div className="crmpe-rail-empty">No personas yet.</div>
          ) : personas.map(p => (
            <button
              key={p.id}
              className={'crmpe-rail-item' + (p.id === selectedId ? ' crmpe-rail-item--on' : '')}
              onClick={() => setSelectedId(p.id)}
            >
              <div className="crmpe-rail-name">{p.display_name}</div>
              <div className="crmpe-rail-sub">
                {p.mailbox_count} mailbox{String(p.mailbox_count) === '1' ? '' : 'es'} · {p.campaign_count} campaign{String(p.campaign_count) === '1' ? '' : 's'}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="crmpe-pane">
        {!detail ? (
          <div className="crmpe-pane-empty">Select a persona, or create one.</div>
        ) : (
          <div className="crmpe-detail">
            <div className="crmpe-hero">
              <div className="crmpe-hero-main">
                <h2 className="crmpe-h2">{detail.display_name}</h2>
                <div className="crmpe-hero-meta">
                  <span className="crmpe-hero-stat"><strong>{backingIds.size}</strong> backing mailbox{backingIds.size !== 1 ? 'es' : ''}</span>
                </div>
              </div>
              <button className="crmpe-btn crmpe-btn--danger crmpe-btn--sm" onClick={deletePersona}>Delete</button>
            </div>

            <div className="crmpe-card">
              <div className="crmpe-card-title">Identity</div>
              <div className="crmpe-fields">
                <BlurField label="Display name" value={detail.display_name} onSave={v => saveDetail({ display_name: v })} />
                <BlurField label="Job title" value={detail.job_title} onSave={v => saveDetail({ job_title: v })} />
                <BlurField label="Reply-to email" value={detail.reply_to_email} onSave={v => saveDetail({ reply_to_email: v })} />
              </div>
            </div>

            <div className="crmpe-card">
              <div className="crmpe-card-title">Signature</div>
              <div className="crmpe-card-sub">Appended when sending as this persona.</div>
              <SignatureEditor
                key={detail.id}
                initial={detail.signature_html}
                clientId={clientId}
                onSave={html => {
                  const tmp = document.createElement('div');
                  tmp.innerHTML = html === '<p></p>' ? '' : html;
                  saveDetail({ signature_html: html === '<p></p>' ? '' : html, signature_text: tmp.textContent || '' });
                }}
              />
            </div>

            <div className="crmpe-card">
              <div className="crmpe-card-title">Backing mailboxes</div>
              <div className="crmpe-card-sub">The engine sends as this persona from these mailboxes. Recipients see one identity.</div>
              {clientMailboxes.length === 0 ? (
                <div className="crmpe-mb-empty">No mailboxes available to this client.</div>
              ) : (
                <div className="crmpe-rows">
                  {[...clientMailboxes].sort((a, b) => (backingIds.has(b.id) ? 1 : 0) - (backingIds.has(a.id) ? 1 : 0)).map(m => {
                    const isBacking = backingIds.has(m.id);
                    return (
                      <div className={'crmpe-row' + (isBacking ? ' crmpe-row--on' : '')} key={m.id}>
                        <span className={'crmpe-row-dot ' + (ST[m.status] || 'crmpe-dot-slate')} />
                        <div className="crmpe-row-id">
                          <div className="crmpe-row-email">{m.email_address}</div>
                          <div className="crmpe-row-sub">
                            <span className="crmpe-row-prov">{m.provider}</span>
                            <span className="crmpe-row-sep">·</span>
                            <span>{m.sent_today ?? 0}/{m.daily_cap ?? '—'} today</span>
                          </div>
                        </div>
                        <button
                          className={'crmpe-toggle' + (isBacking ? ' crmpe-toggle--on' : '')}
                          onClick={() => toggleMailbox(m.id, isBacking)}
                        >
                          {isBacking ? 'Remove' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BlurField({ label, value, onSave }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <div className="crmpe-field">
      <label className="crmpe-lbl">{label}</label>
      <input
        className="crmpe-input"
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={() => { if (v !== (value || '')) onSave(v); }}
      />
    </div>
  );
}

function SignatureEditor({ initial, clientId, onSave }) {
  const [v, setV] = useState(initial || '');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setV(initial || ''); setDirty(false); }, [initial]);
  return (
    <div className="crmpe-sig">
      <ReplyEditor
        value={v}
        onChange={html => { setV(html); setDirty(true); }}
        placeholder="Name, title, contact details, links and disclaimer."
        clientId={clientId}
      />
      <button
        className="crmpe-btn crmpe-btn--primary crmpe-btn--sm crmpe-sig-save"
        onClick={() => { onSave(v); setDirty(false); }}
        disabled={!dirty}
      >
        {dirty ? 'Save signature' : 'Saved'}
      </button>
    </div>
  );
}
